import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  REQUEST_TYPE_LABEL_KEY,
  SupportRequestStatusChip,
  useSupportRequestsQuery,
} from "@/partner/entities/assistant";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { BuyerEmailCell } from "@/partner/shared/components/BuyerEmailCell";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import type { SupportRequestsListQueryType } from "@/types/graphql/partner.generated";
import { buildSupportRequestsFilter } from "../model/buildSupportRequestsFilter";
import { displayName } from "../model/thread";

type RequestRow =
  SupportRequestsListQueryType["supportRequests"]["nodes"][number];

interface SupportRequestsListProps {
  onOpenRequest: (id: string) => void;
}

export const SupportRequestsList: FC<SupportRequestsListProps> = ({
  onOpenRequest,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = useSupportRequestsQuery({
    variables: {
      page,
      rowsPerPage,
      filter: buildSupportRequestsFilter(filter),
    },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.supportRequests?.nodes ?? [];
  const count = data?.supportRequests?.count ?? 0;

  const emptyValue = t("common.emptyDash");

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("requests.empty")} />;
  }

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 64 }}>
              {t("requests.list.numberColumn")}
            </TableCell>
            <TableCell sx={{ minWidth: 240 }}>
              {t("requests.list.subjectColumn")}
            </TableCell>
            <TableCell sx={{ maxWidth: 220 }}>
              {t("requests.viewerColumn")}
            </TableCell>
            <TableCell>{t("requests.statusColumn")}</TableCell>
            {!isMobile && (
              <TableCell>{t("requests.list.assigneeColumn")}</TableCell>
            )}
            <TableCell align="right" sx={{ width: 160 }}>
              {t("requests.list.lastMessageColumn")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: RequestRow) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => onOpenRequest(row.id)}
            >
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {`#${row.number ?? ""}`}
                </Typography>
              </TableCell>

              <TableCell sx={{ minWidth: 240 }}>
                <Typography variant="body2" noWrap>
                  {row.subject}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(REQUEST_TYPE_LABEL_KEY[row.type])}
                </Typography>
              </TableCell>

              <TableCell sx={{ maxWidth: 220 }}>
                <BuyerEmailCell
                  email={row.user?.email}
                  userId={row.user?.id}
                  emptyValue={emptyValue}
                />
              </TableCell>

              <TableCell>
                <SupportRequestStatusChip status={row.status} />
              </TableCell>

              {!isMobile && (
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {displayName(row.assignee) ?? t("requests.card.noAssignee")}
                  </Typography>
                </TableCell>
              )}

              <TableCell align="right" sx={{ width: 160 }}>
                <Typography variant="body2" noWrap>
                  {interpolationFormat(
                    row.lastMessageAt ?? row.createdAt,
                    "short",
                    i18n.language
                  )}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
